/**
 * Common Zod primitives reused across schemas.
 *
 * Use these instead of `z.string()` etc. so every ID, timestamp, email,
 * phone, money, currency is consistent.
 */

import { z } from 'zod';

export const idSchema = z.string().uuid();

export const iso8601Schema = z.preprocess(
  (val) => (val instanceof Date ? val.toISOString() : val),
  z.string().datetime({ offset: true })
);

export const emailSchema = z.string().email().max(254);

/** E.164: +971501234567 */
export const phoneE164Schema = z.string().regex(/^\+[1-9]\d{6,14}$/, 'Phone must be E.164 (e.g. +971501234567)');

export const currencySchema = z.enum(['AED', 'USD', 'GBP', 'PKR', 'SAR', 'EUR', 'INR']);

/** Money as separate fields — minor units (e.g. 12345 = 123.45). */
export const moneySchema = z.object({
  amount: z.number().int().nonnegative(),
  currency: currencySchema,
});

/** Pagination input for list endpoints. */
export const pageInputSchema = z.object({
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).nullish(),
  search: z.string().max(200).nullish(),
});

/** Alias for shorter imports (schemas use `import { pageInput }`). */
const pageInput = pageInputSchema;
export { pageInput };

export const urlSchema = z.string().url().max(2048);

/** Generic sort order. */
export const sortOrderSchema = z.enum(['asc', 'desc']).default('desc');

/** Boolean query parameter — accepts true/false strings or actual booleans. */
export const booleanQuerySchema = z.preprocess(
  (val) => {
    if (val === 'true' || val === '1') return true;
    if (val === 'false' || val === '0') return false;
    return val;
  },
  z.boolean()
);